/**
 * บันทึกกิจกรรม (audit log)
 *
 * ข้อ 5.3 ของสเปคบังคับให้ log ทุก action ที่เปลี่ยนสถานะ ฝั่ง API เขียนลง
 * ตาราง audit_logs มาตั้งแต่ Phase 1 แล้ว (ดู lib/audit.ts) แต่ไม่เคยมีหน้าจอ
 * ให้ดู — ข้อมูลจึงถูกเก็บไว้เฉย ๆ โดยไม่มีใครอ่านได้นอกจากเปิด DB เอง
 *
 * หน้านี้เปิดเฉพาะ ADMIN ขึ้นไป (API ก็บังคับซ้ำอีกชั้นด้วย requireRole('ADMIN'))
 */
import { useState } from 'react';
import { useAuditLogs, type AuditLogEntry } from '../../lib/queries.js';
import {
  Badge, Card, EmptyState, ErrorBox, Loading, PageHeader, Pagination, Select,
  IconListBullet, cx,
} from '../../components/ui/index.js';
import { relative } from '../../lib/format.js';

/**
 * แปล action เป็นภาษาคน
 * เก็บเป็น map ตรง ๆ ไม่ใช้การเดาจากชื่อ เพราะคำแปลที่อ่านรู้เรื่องต้องเขียนเอง
 * ตัวที่ไม่มีในนี้จะแสดง action ดิบ ซึ่งยังอ่านออกและไม่พัง
 */
const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'เข้าสู่ระบบ',
  'auth.register': 'สมัครสมาชิก',
  'auth.change_password': 'เปลี่ยนรหัสผ่าน',
  'auth.revoke_all_sessions': 'ออกจากระบบทุกอุปกรณ์',
  'blog.create': 'สร้างบล็อก',
  'blog.update': 'แก้ไขบล็อก',
  'blog.delete': 'ลบบล็อก',
  'blog.slug_change': 'เปลี่ยน slug บล็อก',
  'blog.theme_change': 'เปลี่ยนธีมบล็อก',
  'post.create': 'สร้างบทความ',
  'post.update': 'แก้ไขบทความ',
  'post.publish': 'เผยแพร่บทความ',
  'post.unpublish': 'ยกเลิกเผยแพร่',
  'post.schedule': 'ตั้งเวลาเผยแพร่',
  'post.delete': 'ลบบทความ',
  'post.restore': 'กู้คืนบทความ',
  'post.duplicate': 'ทำสำเนาบทความ',
  'post.preview': 'สร้างลิงก์พรีวิว',
  'post.submit_review': 'ส่งตรวจ',
  'post.restore_revision': 'ย้อนเวอร์ชันบทความ',
  'category.create': 'สร้างหมวดหมู่',
  'category.update': 'แก้ไขหมวดหมู่',
  'category.delete': 'ลบหมวดหมู่',
  'widget.create': 'สร้าง widget',
  'widget.update': 'แก้ไข widget',
  'widget.delete': 'ลบ widget',
  'widget.domain_add': 'เพิ่มโดเมนที่อนุญาต',
  'widget.domain_remove': 'ลบโดเมนที่อนุญาต',
  'widget.regenerate_key': 'ออกคีย์ใหม่',
  'media.upload': 'อัปโหลดไฟล์',
  'media.delete': 'ลบไฟล์',
  'media.permanent_delete': 'ลบไฟล์ถาวร',
  'member.invite': 'เชิญสมาชิก',
  'member.invite_revoke': 'ยกเลิกคำเชิญ',
  'member.accept_invite': 'รับคำเชิญ',
  'member.update': 'แก้ไขสิทธิ์สมาชิก',
  'member.remove': 'นำสมาชิกออก',
  'tenant.update': 'แก้ไขข้อมูลบริษัท',
};

/** สีตามความรุนแรง — ลบเป็นแดง สร้างเป็นเขียว แก้ไขเป็นน้ำเงิน (tone ที่ Badge รองรับ) */
function toneOf(action: string): 'rose' | 'green' | 'blue' | 'gray' {
  if (/\.(delete|remove|revoke|permanent_delete)$/.test(action)) return 'rose';
  if (/\.(create|upload|invite|register|publish|duplicate)$/.test(action)) return 'green';
  if (/\.(update|change|_change|schedule|restore|regenerate_key|domain_add|domain_remove)$/.test(action)) return 'blue';
  return 'gray';
}

const GROUPS = [
  { value: '', label: 'ทุกกิจกรรม' },
  ...Object.keys(ACTION_LABEL).map((a) => ({ value: a, label: `${ACTION_LABEL[a]} (${a})` })),
];

/** แสดง changes แบบ before → after เฉพาะคีย์ที่เปลี่ยนจริง */
function Changes({ changes }: { changes: AuditLogEntry['changes'] }) {
  if (!changes) return <span className="text-[var(--ink-3)]">—</span>;
  const before = changes.before ?? {};
  const after = changes.after ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  if (!keys.length) return <span className="text-[var(--ink-3)]">—</span>;

  const short = (v: unknown): string => {
    if (v === null || v === undefined) return '—';
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    return s.length > 40 ? `${s.slice(0, 40)}…` : s;
  };

  return (
    <div className="space-y-0.5">
      {keys.slice(0, 4).map((k) => (
        <div key={k} className="flex flex-wrap items-baseline gap-1 text-[11px]">
          <span className="font-medium text-[var(--ink-2)]">{k}</span>
          {k in before && <span className="text-[var(--ink-3)] line-through">{short(before[k])}</span>}
          {k in after && <><span className="text-[var(--ink-3)]">→</span><span className="text-[var(--ink)]">{short(after[k])}</span></>}
        </div>
      ))}
      {keys.length > 4 && (
        <div className="text-[11px] text-[var(--ink-3)]">และอีก {keys.length - 4} ฟิลด์</div>
      )}
    </div>
  );
}

export function AuditLogPage() {
  const [filters, setFilters] = useState({ action: '', page: 1, perPage: 50 });
  const logs = useAuditLogs(filters);

  return (
    <>
      <PageHeader
        title="บันทึกกิจกรรม"
        description="ทุกการกระทำที่เปลี่ยนแปลงข้อมูลในบริษัทนี้ เรียงจากใหม่ไปเก่า"
      />

      <Card className="mb-4 p-4">
        <div className="max-w-md">
          <Select
            label="กรองตามกิจกรรม"
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value, page: 1 }))}
          >
            {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </Select>
        </div>
      </Card>

      {logs.isLoading ? <Loading /> : logs.error ? (
        <ErrorBox error={logs.error} onRetry={() => void logs.refetch()} />
      ) : !logs.data?.data.length ? (
        <EmptyState
          icon={<IconListBullet size={22} />}
          title="ยังไม่มีบันทึก"
          description={filters.action
            ? 'ไม่พบกิจกรรมชนิดนี้ — ลองเลือก "ทุกกิจกรรม"'
            : 'เมื่อมีคนแก้ไขข้อมูลในระบบ รายการจะขึ้นที่นี่'}
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--sunken)] text-left text-xs text-[var(--ink-3)]">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">เมื่อไหร่</th>
                    <th className="px-4 py-2.5 font-medium">ใคร</th>
                    <th className="px-4 py-2.5 font-medium">ทำอะไร</th>
                    <th className="px-4 py-2.5 font-medium">กับอะไร</th>
                    <th className="px-4 py-2.5 font-medium">เปลี่ยนอะไรบ้าง</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--line-soft)]">
                  {logs.data.data.map((row) => (
                    <tr key={row.id} className="align-top">
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--ink-3)]">
                        {relative(row.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        {row.user ? (
                          <>
                            <div className="text-[var(--ink)]">{row.user.fullName}</div>
                            <div className="text-[11px] text-[var(--ink-3)]">{row.user.email}</div>
                          </>
                        ) : (
                          <span className="text-[var(--ink-3)]">ระบบ</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={toneOf(row.action)}>
                          {ACTION_LABEL[row.action] ?? row.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {row.entityType ? (
                          <>
                            <div className="text-[var(--ink-2)]">{row.entityType}</div>
                            {row.entityId && (
                              <div className={cx('font-mono text-[10px] text-[var(--ink-3)]')}>{row.entityId}</div>
                            )}
                          </>
                        ) : <span className="text-[var(--ink-3)]">—</span>}
                      </td>
                      <td className="px-4 py-3"><Changes changes={row.changes} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            page={logs.data.meta.page}
            totalPages={logs.data.meta.totalPages}
            onChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        </>
      )}
    </>
  );
}
